import { ISetting } from '../interfaces/settings.interface';
import { UtilService } from '../../shared/services/util.services';
import { PopupService } from '../../shared/services/popup.service';
import { JobService } from '../../shared/services/job.service';
import { BadgeService } from '../../shared/services/badges.service';
import { EditPartialUserInfoComponent } from 'src/modules/shared/components/edit-partial-user-info/edit-partial-user-info.component'
import { getUserInfoAction, getUserInfoActionWithoutLoader, updateUserPartialState } from 'src/modules/authentication/+store/actions/auth.actions';
import { Store } from '@ngrx/store';
import { IAuthState } from 'src/modules/authentication/+store/auth.state';
import { AlertController, ModalController } from '@ionic/angular';
import { Injectable, Output } from '@angular/core';
import * as moment from 'moment';
import { OnBoardingStafferService } from 'src/modules/onboarding-staffer/services/onboarding-staffer.service';
import { forEach } from 'lodash';
import { ITalentJob } from '../interfaces/talent-job.interface';
import { CertificateValidationRulesService } from 'src/modules/shared/services/certificate-validation-rules.service';
import { AttestationAndDeclarationService } from 'src/modules/shared/services/attestation-and-declaration.service';
import { Router } from '@angular/router';
import { IUser } from 'src/modules/authentication/interfaces/user.interface';
import { LoadingService } from 'src/modules/shared/services/loading.service';
import { CertificateService } from 'src/modules/shared/services/certificate.service';
import { UserService } from 'src/modules/shared/services/user.service';
import { DownloadService } from 'src/modules/shared/services/download.service';

@Injectable({
  providedIn: 'root'
})
export class TalentJobChecksService {
  settings: ISetting;
  userData: any;
  // TODO think of a better way to do this
  updateUser: any;

  constructor(
    // private talentDashboardUtilService: TalentDashboardUtilService,
    // private imagesService: ImagesService,
    private utilService: UtilService,
    private modalController: ModalController,
    private popupService: PopupService,
    private jobService: JobService,
    private alertController: AlertController,
    private badgeService: BadgeService,
    private authStore: Store<IAuthState>,
    private onBoardingStafferService: OnBoardingStafferService,
    private certificateValidationRulesService: CertificateValidationRulesService,
    private attestationAndDeclarationService: AttestationAndDeclarationService,
    private loadingService: LoadingService,
    private router: Router,
    private certificateService: CertificateService,
    private userService: UserService,
    private downloadService: DownloadService,
    private store: Store<IAuthState>
  ) {}

  async performChecks(jobItem, settings: ISetting[], user, updateUser): Promise<{ canApply: boolean; rcmpAccepted: boolean }> {
    this.userData = { ...user };
    this.settings = { ...settings[0] };
    this.updateUser = updateUser;
    const result = { canApply: true, rcmpAccepted: false, profileApproved: false };
    
    try {

      if(!user.profile_approved && !user.isSignupProcessComplete) {
        return result;
      }

    
      if(!user.profile_approved && user.isSignupProcessComplete) {
        result.canApply = false;
        await this.underReviewPopup()
      }

      // Check if user has already applied on the job or not
      this.EnsureNotAlreadyApplied(jobItem);

      await this.ensureRecurringWarning(jobItem);

      // check if the vss attestation is enabled
      if (this.settings.enableVssAttestation) {
        // it is
        await this.checkVSS();
      }

      // Show Job Disclaimer
      await this.ensureDisclaimerAcceptance(jobItem);

      // Inform the worker of any apparel/safety-shoe this client requires for the shift
      await this.ensureApparelRequirements(jobItem);

      // If the job demands 14 days of isolation, ensure that user meet that
      // criteria
      // await this.Ensure14DaysCheck(jobItem);

      // Ensure that user signs waiver of liability form
      await this.EnsureWaiverOfLiability(jobItem);

      // Ask to perform RCMP / background check if required
      result.rcmpAccepted = await this.askForRcmpPermission(jobItem);

      // check if badge required for the shift
      await this.requestForBadgeIfRequired(this.userData, jobItem);

      //Check if VSC is uploaded
      await this.ensureContractorHasValidDocuments(jobItem, this.userData);

      // Refresh the user in the background WITHOUT a loader. getUserInfoAction shows the shared
      // singleton loader via its effect; on a slow network that me() call is still in flight while
      // the apply flow presents its "Shift requested" popup, and (the loader isn't ref-counted) it
      // gets stranded on top of that popup — the app looks frozen. profileApproved is read from the
      // already-loaded userData below, so this refresh only needs to sync the store, not block UI.
      this.authStore.dispatch(getUserInfoActionWithoutLoader());
      result.profileApproved = this.userData.profile_approved;

      // free up memory and clear the variables
      this.userData = null;
      this.settings = null;
      this.updateUser = null;

      return result;
    } catch (err) {
      // any check failed
      console.log('error: ', err);
      // Safety net: a failed check (e.g. missing apparel) must never leave a loader
      // running behind the blocking popup. Clear any spinner left by an upstream check
      // or a still-in-flight getUserInfoAction before returning.
      await this.loadingService.dismissAll();
      return { ...result, canApply: false };
    }
  }

  // Apparel / safety-shoe requirements are client-managed at the shift level (entity default
  // or per-shift override). Apparel uploads are otherwise optional. Here we inform the worker
  // — and block, mirroring the backend — when this shift's client requires apparel/equipment
  // the worker has not uploaded.
  private readonly APPAREL_BUCKETS = {
    uniformPictureFOH: ['black_shirt', 'white_shirt', 'black_pants', 'black_shoes', 'black_socks', 'foh_kit', 'black_belt', 'black_vest', 'black_tie'],
    uniformPictureBOH: ['non_slip_shoes', 'kitchen_black_white', 'white_chef_jacket', 'black_chef_jacket', 'knife_kit'],
    uniformPictureGeneral: ['safety_shoes']
  };

  private readonly APPAREL_LABELS = {
    black_shirt: 'Black Dress Shirt', white_shirt: 'White Dress Shirt', black_pants: 'Black Pants',
    black_shoes: 'Black Dress Shoes', black_socks: 'Black Socks', foh_kit: 'FOH Kit',
    black_belt: 'Black Belt', black_vest: 'Black Vest', black_tie: 'Black Tie',
    non_slip_shoes: 'Non Slip Shoes', kitchen_black_white: 'Kitchen Blacks/Whites',
    white_chef_jacket: 'White Chef Jacket', black_chef_jacket: 'Black Chef Jacket',
    knife_kit: 'Knife Kit', safety_shoes: 'Safety Shoes'
  };

  // Resolve the apparel requirement for a shift from the client's entity-level default.
  // Safety shoes are just the 'safety_shoes' apparel item in requiredApparel.
  private async resolveApparelRequirement(jobItem): Promise<{ requiredApparel: string[] } | null> {
    const normalize = (raw) => {
      if (!raw) return null;
      const requiredApparel = Array.isArray(raw.requiredApparel) ? raw.requiredApparel.slice() : [];
      return requiredApparel.length ? { requiredApparel } : null;
    };

    // The entity snapshot embedded in the shift list can be stale — the client may have
    // added/removed apparel (or deleted a role) after the list was loaded. Prefer live
    // config; fall back to the snapshot on any fetch failure so a network hiccup never
    // blocks a valid apply.
    let config = jobItem && jobItem.entity && jobItem.entity.apparelRequirements;
    const entityId = jobItem && jobItem.entity && jobItem.entity._id;
    if (entityId) {
      try {
        const freshConfig: any = await this.jobService.getEntityApparelRequirements(entityId);
        if (freshConfig) {
          config = freshConfig;
        }
      } catch (e) {
        console.log('apparel: fresh apparel-requirements fetch failed, using shift snapshot', e);
      }
    }

    if (config && config.enabled && Array.isArray(config.requirements)) {
      const match = config.requirements.find(r => r.skill === jobItem.skill);
      return normalize(match);
    }
    return null;
  }

  private getApparelStatus(user, key): string {
    for (const bucket of Object.keys(this.APPAREL_BUCKETS)) {
      if (this.APPAREL_BUCKETS[bucket].indexOf(key) > -1) {
        const pics = user && user[bucket];
        return pics && pics[key] ? pics[key].status : undefined;
      }
    }
    return undefined;
  }

  private async ensureApparelRequirements(jobItem): Promise<void> {
    const requirement = await this.resolveApparelRequirement(jobItem);
    if (!requirement) return;

    const missing = requirement.requiredApparel.filter(key => {
      const status = this.getApparelStatus(this.userData, key);
      return !status || status === 'not uploaded';
    });
    if (!missing.length) return;

    const labels = missing.map(key => this.APPAREL_LABELS[key] || key);
    // Clear any loader left running by an upstream check so it can't sit stuck behind the popup.
    await this.loadingService.dismissAll();
    // Await the popup so it is fully presented and dismissed by the user before the apply flow
    // continues — otherwise the fire-and-forget modal races with the shift-details modal being
    // dismissed and can be left stuck on screen.
    await this.popupService.showModal({
      heading: 'Apparel required for this client',
      message: `This client requires the following for this shift: ${labels.join(', ')}. Please upload ${missing.length > 1 ? 'these items' : 'this item'} to apply.`,
      btn: 'Upload',
      navigateRoute: 'talent-dashboard/upload-equipment',
      imgURL: 'assets/images/notice.png',
      // Let the worker close the popup without being forced onto the Apparel screen.
      showDismissButton: true
    });
    throw new Error('apparel requirements not met');
  }

  // Check ID badge criteria and apply for badge
  private async requestForBadgeIfRequired(user, jobItem){
    
    // Check ID badge criteria
    const checkResult = await this.performChecksForIdBadge(user, jobItem);
    if(!checkResult.badgeRequired){
      // contractor has the badge or its not required
      return;
    }

    // Show promot for ID badge
    const userResponse = await this.showPromptForIDBadge(checkResult.badgeName ? `${checkResult.badgeName} required` : 'Badge required:');
    
    // download badge
    if(userResponse == false){
      await this.downloadPhysicalBadge(jobItem);
    }
    
    throw new Error('Badge required!');
    // User now have a badge printed, updating user.
    // await this.badgeService.updatePhysicalBadge(user._id, true);
    // this.store.dispatch(updateUserPartialState({  partialUserState: { hasPhysicalBadge: true } }))
  }

  async downloadPhysicalBadge(job) {
    const loading = await this.loadingService.showLoading().toPromise();
    try {
      const badge = await this.userService.getPhysicalBadge(this.userData._id, job.skill, job.entity._id);
      const pdfData = badge.attachment;
      this.downloadService.downloadPdf(pdfData, 'physicalBadge');
    } catch (error) {
      console.log('err', error);
      this.popupService.showModal({
        heading: 'Error',
        message: typeof error.error === 'string' ? error.error : 'Download failed. Please try again.',
        btn: 'Dismiss',
        navigateRoute: null,
        imgURL: 'assets/images/sorry1.png',
        blurBackground: true
      });
    } finally {
      await this.loadingService.hideLoading(loading);
    }
  }

  
  // Show prompt for ID badge
  private async showPromptForIDBadge(heading = 'Shift requires badge'){
    return new Promise(async (resolve) => {
          const message = `This shift requires a physical badge. Please download the file and have it printed at your local TPH store or a similar service provider. Request laminated cardboard or plastic. Send us a photo via SMS or email once printed along with a receipt.  Please note that any printing costs will be your responsibility, and while we appreciate your efforts, we’re unable to guarantee shift availability. Thank you for your understanding.`;
          this.popupService.showModal(
            {
              heading,
              message: `${message}`,
              btn: 'Download',
              rejectBtnText: 'Cancel',
              navigateRoute: null,
              imgURL: 'assets/images/sorry1.png',
              blurBackground: true
            },
            async () => {
              // download the badge
              resolve(false);
            }
      );
    })
  }

  private async performChecksForIdBadge(user, jobItem){

  
      if(
        //user.hasPhysicalBadge || 
      // entity doesn't require physical badge
        !jobItem.entity.requiresPhysicalBadge || 
      // user doesn't have healthcare skill
        !this.utilService.doesSkillRequirePhysicalBadge(jobItem.skill)){
        return {badgeRequired: false};
      }

      // if entity does require a physical badge and it doesn't require a custom badge
      // and user already has a physical badge
      if(!jobItem.entity.clientSpecificBadge?.isEnabled && user.hasPhysicalBadge){
        return {badgeRequired: false};
      }

      // if entity requires a custom badge and user has that badge
      if(jobItem.entity.clientSpecificBadge?.isEnabled && user.clientBadges.indexOf(jobItem.entity.clientSpecificBadge?.badgeName ) > -1){
        return {badgeRequired: false};
      }
      
      // user doesn't have any badge

      // Get badges
      const badgeResp = await this.badgeService.getBadges(user._id);

          // Check length of badges
      if(badgeResp && badgeResp.badges && badgeResp.badges.length) return {badgeRequired: false};

      if(jobItem.entity.clientSpecificBadge?.isEnabled){
        return {badgeRequired: true, badgeName: jobItem.entity.clientSpecificBadge?.badgeName || null};
      }

      return {badgeRequired: true};

  }

  private async ensureRecurringWarning(jobItem): Promise<void> {

      // show warning for Recurring
      let shouldContinueAfterWarning;
      if (jobItem.recurringShift.isTrue && this.settings.contractorNeedsToWorkAllShiftsInRecurringWarning) {
        shouldContinueAfterWarning = await this.popupService.recurringWarning();
      }

      if (jobItem.recurringShift.isTrue && shouldContinueAfterWarning === false) {
        throw new Error('PRESSED_CANCEL_ON_RECURRING_WARNING');;
      } 
  }
  private async ensureDisclaimerAcceptance(jobItem): Promise<void> {
    return new Promise((resolve, rejectPromise) => {
      if (!jobItem.disclaimer || !jobItem.disclaimer.length) {
        return resolve();
      }

      const data = {
        action: '',
        jobId: jobItem._id,
        entityId: jobItem.entity._id,
        email: this.userData.email,
        firstName: this.userData.firstName,
        lastName: this.userData.lastName
      };

      this.popupService.showModal(
        {
          heading: 'Notice',
          message: jobItem.disclaimer,
          btn: 'Confirm',
          rejectBtnText: 'Reject',
          navigateRoute: null,
          imgURL: null,
          blurBackground: true
        },
        () => {
          data.action = 'accept';
          this.jobService.logDisclaimer(data);
          resolve();
        },
        () => {
          data.action = 'reject';
          this.jobService.logDisclaimer(data);
          rejectPromise();
        }
      );
    });
  }

  private underReviewPopup(): void {
    this.popupService.showModal({
      heading: 'Your account is pending approval',
      message: 'Our team is reviewing your profile. You will be able to apply for shifts once your account has been approved.',
      btn: 'Got it',
      navigateRoute: 'talent-dashboard/home',
      imgURL: 'assets/images/profile.png'
    });
  }

  private EnsureNotAlreadyApplied(jobItem): void {
    if (jobItem.isApplied) {
      // this.availableLoadedJobs[jobIndex].isApplied = true;
      this.popupService.showPopup('Error', 'You have already applied for this shift.');
      throw new Error('ALREADY_APPLIED');
    }
    return;
  }

  private Ensure14DaysCheck(jobItem): void {
    // 14 days check
    if (
      jobItem.entity.creator.companyType === 'Healthcare' &&
      this.userData.lastJobCompleted &&
      !this.userData.lastJobCompleted.byPass &&
      this.userData.lastJobCompleted.companyId !== jobItem.entity._id &&
      !jobItem.entity.creator.disable14DayCheckOnClient &&
      !(this.userData.badges.indexOf('covid-19-vaccine') > -1) &&
      this.utilService.not14daysPast(jobItem.shiftStartTime, this.userData.lastJobCompleted.workedOn)
    ) {
      this.popupService.showModal({
        heading: 'Sorry',
        message:
          'This posting has a single-site order in effect. You must isolate for 14 days after your last shift and provide a negative COVID test taken within 7 days of the end of your isolation period. If you are fully vaccinated, please upload your proof of vaccination. The isolation requirement may be waived.',
        btn: 'Ok',
        navigateRoute: null,
        imgURL: 'assets/images/sorry1.png'
      });
      throw new Error('14_DAYS_CHECK_FAILED');
    }
  }

  private EnsureWaiverOfLiability(jobItem): void {
    // Waiver of Liability / Hello Sign
    if (!this.userData.hasAccessToApplyOnShifts) {
      this.popupService.showModal({
        heading: 'Please sign the Waiver of Liability',
        message: 'You need to sign the Waiver of Liability before you can apply to this shift.',
        btn: 'Ok',
        navigateRoute: 'talent-dashboard/hello-sign',
        imgURL: 'assets/images/profile.png',
        blurBackground: true
      }, () => {
        this.hideNotificationPopup();
      });

      throw new Error('WAIVER_LIABILITY_NOT_SIGNED');
    }
  }

  private async askForRcmpPermission(jobItem): Promise<boolean> {
    return false;
  }

  private async checkVSS(): Promise<void> {
    return new Promise((resolve, rejectPromise) => {
      if (
        this.userData.vss &&
        // if user has already submitted vss
        this.userData.vss.submitted_on &&
        // and its not 12 months old
        this.utilService.not12monthsPassed(this.userData.vss.submitted_on) &&
        // and its accespted
        this.userData.vss.accepted
      ) {
        // we are fine, user can apply
        resolve();
      } else {
        // ask to fill vss
        this.popupService.showModal(
          {
            heading: 'VSS ATTESTATION',
            message: `Do you attest to the following?`,
            list: [
              `Do you attest that you have not been charged with, or convicted of, a criminal offence that would preclude you from working with, or around a “vulnerable person",   as defined in section 6.3 of the Criminal Records Act, R.S.C. 1985, c. C-47 as “… a person who, because of his or her age,    a disability or other circumstances, whether temporary or permanent`,
              `(a) is in a position of dependency on others; or `,
              `(b) is otherwise at a greater risk than the general population of being harmed by a person in a position of trust or authority towards them?`
            ],
            btn: 'Yes, you attest',
            rejectBtnText: 'No, you don\'t attest',
            navigateRoute: null,
            imgURL: null,
            blurBackground: true
          },
          async accept => {
            // Attestation Accepted
            await this.onBoardingStafferService.updateUser(this.userData._id, {vss: {
              accepted: true,
              submitted_on: moment().unix()
            }}) 
            this.popupService.showModal(
              {
                heading: 'Attestation accepted.',
                message: 'You can continue applying jobs now',
                btn: 'Ok',
                navigateRoute: null,
                imgURL: 'assets/images/thumbs-up.png',
                blurBackground: true
              },
              () => {
                resolve();
              }
            );
          },
          async rejected => {
            // Attestation Rejected
            this.popupService.showModal(
              {
                heading: 'VSS Attestation',
                message: `You must attest that you are free of criminal convictions to apply for this position.`,
                btn: 'Ok',
                navigateRoute: null,
                imgURL: 'assets/images/profile.png',
                blurBackground: true
              },
              () => {
                rejectPromise();
              }
            );
          }
        );
      }
    });
  }

  private async askForRCMP(): Promise<boolean> {
    return new Promise(async (resolve, rejectPromise) => {
      const alert = await this.alertController.create({
        header: 'RCMP check required',
        cssClass: 'alert-modal',
        message: `Please note this client required an RCMP check in order to qualify.
         There will be a $50 charge that will charged to your next invoice. 
         You'll also be emailed a copy, and a badge will appear on your profile. This is a one-time charge.`,
        buttons: [
          {
            text: 'Reject',
            cssClass: 'danger',
            handler: () => {
              rejectPromise();
            }
          },
          {
            text: 'Accept',
            handler: () => {
              resolve(true);
            }
          }
        ]
      });

      alert.present();
    });
  }

  public async ensureContractorHasValidDocuments(jobItem: ITalentJob, userData: IUser): Promise<boolean> {
    return new Promise(async (resolve, rejectPromise) => {
      const timeZone = this.utilService.getTimezone(userData.address.province);
      const loading = await this.loadingService.showLoading().toPromise();

      try {
        // Skip VSC check if skill doesn't require it
        if (!this.utilService.isVSCRequired(jobItem.skill)) {
          await this.loadingService.hideLoading(loading);
          resolve(false);
          return;
        }

        // Single API call to get all VSC validation data
        const vscStatus = await this.attestationAndDeclarationService.getVSCValidationStatus(userData._id);

        // Check if bypass is enabled
        if (vscStatus.byPassVSCFlow) {
          await this.loadingService.hideLoading(loading);
          resolve(false);
          return;
        }

        // Step 1: VSC within 6 months from issue date → allow without declaration
        if (vscStatus.hasVSCWithin6Months) {
          await this.loadingService.hideLoading(loading);
          resolve(false);
          return;
        }

        // Step 2: VSC within 3 years from issue date → check declarations
        if (vscStatus.hasVSCWithin3Years) {
          const isDeclarationValid = this.certificateValidationRulesService.checkIfDeclarationIsValid(vscStatus.vscDeclarationExpiry, timeZone);
          await this.loadingService.hideLoading(loading);

          if (isDeclarationValid) {
            resolve(false);
          } else {
            rejectPromise();
            await this.showDeclarationPopup();
          }
          return;
        }

        // Step 3: No valid VSC within 3 years → check for valid Receipt of VSC (8 weeks)
        await this.loadingService.hideLoading(loading);

        if (vscStatus.hasReceiptWithin8Weeks) {
          // Has valid receipt but no VSC → caller will trigger background check payment popup
          resolve(true);
          return;
        }

        // No valid VSC and no valid receipt → show popup that VSC is not uploaded
        this.reuploadCertificate();
        rejectPromise();
      } catch (error) {
        await this.loadingService.hideLoading(loading);
        rejectPromise();
      }
    });
  }

  hideNotificationPopup() {
    const elements = document.getElementsByClassName('notificationListModal');
    
    if(elements && elements.length) {
      forEach(elements, (elem) => {
        if(elem['style']){
          elem['style'].display = 'none';
        }
      })
    }
  }

  reuploadCertificate() {
    this.popupService.showModal(
      {
        heading: 'Vulnerable Sector Check Required',
        message: `Before requesting this shift, please upload valid Vulnerable Sector Check credential`,
        btn: 'Upload',
        rejectBtnText: 'Dismiss',
        navigateRoute: null,
        imgURL: 'assets/images/notice.png',
        blurBackground: true
      },
      async accept => {
       //Redirect to profile to upload certificate / receipt if no valid VSC or receipt found
       this.hideNotificationPopup();
       this.router.navigateByUrl('talent-dashboard/profile');
        return;
      },

      async rejected => {
        return;
      }
    );
  }

  async showDeclarationPopup() {
    await this.popupService.showModal(
      {
        heading: 'Please complete the Vulnerable Sector Check Declaration',
        message: 'To continue applying to shifts, please complete the Vulnerable Sector Check Declaration',
        btn: 'Ok',
        rejectBtnText: '',
        navigateRoute: null,
        imgURL: 'assets/images/notice.png',
        blurBackground: true
      },  () => {
        // Navigate from the dismiss callback so the router runs inside NgZone
        // after the modal is fully dismissed. Firing navigateByUrl directly from
        // the click handler can leak out of NgZone on newer Android WebViews,
        // causing the modal to close but the navigation to silently not commit.
        this.hideNotificationPopup();
        this.router.navigateByUrl('/talent-dashboard/sign-declaration');
      }
    );
  }
}
